import { hasUserAccessFunctionality } from 'helpers/PermissionHelper';
import { t } from 'i18next';
import PageHeader from 'modules/admin/components/_common/Header/PageHeader';
import { useMemo } from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { adminModuleCountSelector } from 'store/admin/selectors/adminModuleCountSelectors';
import { useAppSelector } from 'store/hooks';

interface TabItem {
  slug: string;
  description?: string;
  permissions?: string;
  title?: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
  // extra check on top of permissions, e.g. the Merchandise tab
  isVisible?: () => boolean;
}

interface AdminTabLayoutProps {
  tabs: TabItem[];
  childPaths?: string[];
  // lets a module scope header/tab styles to itself without touching other modules
  className?: string;
}

export default function AdminTabLayout({ tabs, childPaths = [], className }: AdminTabLayoutProps) {
  const location = useLocation();
  const counts = useAppSelector(adminModuleCountSelector);

  const allowedTabs = useMemo(() => {
    return tabs.filter(
      (tab) =>
        hasUserAccessFunctionality(tab?.permissions ?? []) && (!tab.isVisible || tab.isVisible())
    );
  }, [tabs]);
  const activeTab = useMemo(() => {
    // Longest-prefix match, not first-in-array - some tab groups have one tab's path
    // nested under another's (e.g. .../product-attribute vs .../product-attribute/fixed),
    // so the first match in array order isn't necessarily the right one.
    const matches = allowedTabs.filter((tab) => location.pathname.startsWith(tab.path));
    if (matches.length > 0) {
      return matches.reduce((longest, tab) =>
        tab.path.length > longest.path.length ? tab : longest
      );
    }
    const isChildPath = childPaths.some((p) => location.pathname.startsWith(p));
    if (isChildPath) return allowedTabs[0];
    return undefined;
  }, [allowedTabs, location.pathname, childPaths]);

  const activeCount = useMemo(() => {
    if (!activeTab) return 0;
    return counts?.[activeTab.slug] ?? 0;
  }, [activeTab, counts]);

  if (allowedTabs.length === 0) {
    return <div>You do not have permission to access this module.</div>;
  }

  if (!activeTab) {
    return <Navigate to={allowedTabs[0].path} replace />;
  }

  return (
    <div className={className}>
      <PageHeader
        title={activeTab?.title ? t(`${activeTab?.title}`) : t(`${activeTab?.label ?? ''}`)}
        description={t(`${activeTab?.description ?? ''}`)}
        count={activeCount}
      />
      {allowedTabs.length > 1 && (
        <div className="module-tabs">
          {allowedTabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              // A plain string here would let NavLink's own prefix-based isActive
              // (not our longest-prefix-aware activeTab) auto-append "active" too -
              // e.g. .../product-attribute/fixed starts with .../product-attribute,
              // so both tabs would show active. A function bypasses that entirely.
              className={() => (activeTab?.path === tab.path ? 'tab active' : 'tab')}
            >
              {tab.icon && <span className="tab-icon">{tab.icon}</span>}
              {t(`${tab.label ?? ''}`)}
            </NavLink>
          ))}
        </div>
      )}
      <Outlet />
    </div>
  );
}
